import React, { useState, useEffect, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { 
  Play, 
  Pause, 
  X, 
  Download, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Image as ImageIcon,
  Archive,
  RefreshCcw,
  Trash2,
  Eye,
  ZoomIn
} from "lucide-react";
import { useImageQueue, QueueItem } from "@/hooks/useImageQueue";
import { useSecureAuthContext } from "@/contexts/SecureAuthContext";
import { toast } from "sonner";
import JSZip from 'jszip';

// Clean model display name by removing provider information
const cleanModelDisplayName = (modelName: string | undefined) => {
  if (!modelName) return 'Unknown Model';
  return modelName
    .replace(/\s*\(.*?\)/g, '') // Remove anything in parentheses
    .replace(/\s*-\s*(P1|P2|P3|P4|A4F|Via\s+provider\s+\d+)/gi, '') // Remove provider info
    .replace(/\s*-\s*Provider\s+\d+/gi, '') // Remove "Provider X" patterns
    .replace(/\s*-\s*A4F$/gi, '') // Remove trailing A4F
    .replace(/\s*via\s+provider\s+\d+/gi, '') // Remove "via provider X" patterns
    .replace(/\s*\|\s*Provider\s+\d+/gi, '') // Remove "| Provider X" patterns
    .replace(/\s*\[\s*P\d+\s*\]/gi, '') // Remove [P1], [P2] etc patterns
    .replace(/\s*\(\s*P\d+\s*\)/gi, '') // Remove (P1), (P2) etc patterns
    .replace(/provider-\d+\/*/gi, '') // Remove provider-1/, provider-2/, etc.
    .replace(/\s+/g, ' ') // Clean up extra spaces
    .trim();
};

const QueueMonitor = () => {
  const { user } = useSecureAuthContext();
  const { queue, isProcessing, removeFromQueue, resumeItem, pauseItem, clearQueue } = useImageQueue(user?.id);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [selectedQueueImages, setSelectedQueueImages] = useState<{[queueId: string]: Set<string>}>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [viewingImage, setViewingImage] = useState<string | null>(null);

  console.log('🔍 QueueMonitor - User:', user?.id);
  console.log('🔍 QueueMonitor - Queue length:', queue.length);
  console.log('🔍 QueueMonitor - Is processing:', isProcessing);

  const { activeItems, completedItems, errorItems } = useMemo(() => {
    return {
      activeItems: queue.filter(item => ['pending', 'processing', 'paused'].includes(item.status)),
      completedItems: queue.filter(item => item.status === 'completed'),
      errorItems: queue.filter(item => item.status === 'error')
    };
  }, [queue]);

  const handlePauseResume = (item: QueueItem) => {
    if (item.status === 'processing' || item.status === 'pending') {
      if (pauseItem) {
        pauseItem(item.id);
      } else {
        toast.info("Pause functionality will be available in the next update");
      }
    } else if (item.status === 'error' || item.status === 'paused') {
      resumeItem(item.id);
    }
  };

  const handleCancel = (item: QueueItem) => {
    removeFromQueue(item.id);
  };

  const handleImageSelect = (imageUrl: string, checked: boolean) => {
    const newSelected = new Set(selectedImages);
    if (checked) {
      newSelected.add(imageUrl);
    } else {
      newSelected.delete(imageUrl);
    }
    setSelectedImages(newSelected);
  };

  const handleQueueImageSelect = (queueId: string, imageUrl: string, checked: boolean) => {
    const queueImages = selectedQueueImages[queueId] || new Set();
    if (checked) {
      queueImages.add(imageUrl);
    } else {
      queueImages.delete(imageUrl);
    }
    setSelectedQueueImages(prev => ({
      ...prev,
      [queueId]: queueImages
    }));
  };

  const handleSelectAllQueue = (queueId: string, images: string[], checked: boolean) => {
    const queueImages = new Set<string>();
    if (checked) {
      images.forEach(imageUrl => queueImages.add(imageUrl));
    }
    setSelectedQueueImages(prev => ({
      ...prev,
      [queueId]: queueImages
    }));
  };

  const handleSelectAll = (images: string[], checked: boolean) => {
    const newSelected = new Set(selectedImages);
    images.forEach(imageUrl => {
      if (checked) {
        newSelected.add(imageUrl);
      } else {
        newSelected.delete(imageUrl);
      }
    });
    setSelectedImages(newSelected);
  };

  const downloadImage = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  const handleBulkDownload = async (imageUrls: string[], asZip: boolean = false) => {
    if (imageUrls.length === 0) {
      toast.error('No images selected for download');
      return;
    }

    setIsDownloading(true);

    try {
      if (asZip) {
        const zip = new JSZip();
        
        for (let i = 0; i < imageUrls.length; i++) {
          const imageUrl = imageUrls[i];
          try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const filename = `generated-image-${i + 1}.${blob.type.split('/')[1] || 'png'}`;
            zip.file(filename, blob);
          } catch (error) {
            console.warn(`Failed to add image ${i + 1} to zip:`, error);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `generated-images-${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`Downloaded ${imageUrls.length} images as ZIP`);
      } else {
        for (let i = 0; i < imageUrls.length; i++) {
          const imageUrl = imageUrls[i];
          const filename = `generated-image-${Date.now()}-${i + 1}.png`;
          await downloadImage(imageUrl, filename);
          
          if (i < imageUrls.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        toast.success(`Downloaded ${imageUrls.length} images individually`);
      }
    } catch (error) {
      console.error('Error in bulk download:', error);
      toast.error('Failed to complete bulk download');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleQueueBulkDownload = async (queueId: string, asZip: boolean = false) => {
    const queueImages = selectedQueueImages[queueId];
    if (!queueImages || queueImages.size === 0) {
      toast.error('No images selected for download');
      return;
    }

    await handleBulkDownload(Array.from(queueImages), asZip);
    
    // Clear selection after download
    setSelectedQueueImages(prev => ({
      ...prev,
      [queueId]: new Set()
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/80';
      case 'processing': return 'bg-blue-500/80';
      case 'completed': return 'bg-green-500/80';
      case 'error': return 'bg-red-500/80';
      case 'paused': return 'bg-gray-500/80';
      default: return 'bg-gray-500/80';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <RefreshCcw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const renderQueueItem = (item: QueueItem) => {
    const progress = item.totalImages > 0 ? (item.processedImages / item.totalImages) * 100 : 0;
    const generatedImageUrls = item.generatedImages.map(img => img.url).filter(Boolean);
    const queueSelectedImages = selectedQueueImages[item.id] || new Set();

    return (
      <div key={item.id} className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-2xl p-6 backdrop-blur-sm hover:border-white/30 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Badge className={`${getStatusColor(item.status)} text-white px-3 py-1 border-0`}>
              <div className="flex items-center space-x-2">
                {getStatusIcon(item.status)}
                <span className="capitalize font-medium">{item.status}</span>
              </div>
            </Badge>
            <span className="text-sm text-gray-300 bg-white/10 px-2 py-1 rounded-lg">
              {item.processedImages}/{item.totalImages} images
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {(item.status === 'processing' || item.status === 'error' || item.status === 'paused' || item.status === 'pending') && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePauseResume(item)}
                className="border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white"
              >
                {item.status === 'processing' || item.status === 'pending' ? (
                  <>
                    <Pause className="w-4 h-4 mr-1" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-1" />
                    Resume
                  </>
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleCancel(item)}
              className="border-red-500/50 hover:border-red-500 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-black/20 p-4 rounded-xl border border-white/10">
            <p className="text-sm text-gray-400 mb-2 font-medium">Prompt:</p>
            <p className="text-white text-sm leading-relaxed">
              {item.prompt.length > 150 ? `${item.prompt.substring(0, 150)}...` : item.prompt}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white/5 p-3 rounded-lg border border-white/10">
              <span className="text-gray-400 block mb-1">Model:</span>
              <span className="text-white font-medium">{cleanModelDisplayName(item.model)}</span>
            </div>
            <div className="bg-white/5 p-3 rounded-lg border border-white/10">
              <span className="text-gray-400 block mb-1">Size:</span>
              <span className="text-white font-medium">{item.size}</span>
            </div>
            <div className="bg-white/5 p-3 rounded-lg border border-white/10">
              <span className="text-gray-400 block mb-1">Quality:</span>
              <span className="text-white font-medium">{item.quality}</span>
            </div>
            <div className="bg-white/5 p-3 rounded-lg border border-white/10">
              <span className="text-gray-400 block mb-1">Style:</span>
              <span className="text-white font-medium">{item.style}</span>
            </div>
          </div>

          {progress > 0 && (
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400 font-medium">Progress</span>
                <span className="text-white font-bold">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-3 bg-white/10" />
            </div>
          )}

          {item.error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300 font-medium">Error Details</span>
              </div>
              <p className="text-red-300 text-sm">{item.error}</p>
            </div>
          )}

          {generatedImageUrls.length > 0 && (
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-semibold flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5" />
                  <span>Generated Images ({generatedImageUrls.length})</span>
                </h4>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={generatedImageUrls.every(url => queueSelectedImages.has(url))}
                      onCheckedChange={(checked) => handleSelectAllQueue(item.id, generatedImageUrls, checked as boolean)}
                      className="border-white/30"
                    />
                    <span className="text-sm text-gray-400">Select All</span>
                  </div>
                  {queueSelectedImages.size > 0 && (
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQueueBulkDownload(item.id, false)}
                        disabled={isDownloading}
                        className="border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Individual
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQueueBulkDownload(item.id, true)}
                        disabled={isDownloading}
                        className="border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white"
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        ZIP
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {generatedImageUrls.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <div className="absolute top-2 left-2 z-10">
                      <Checkbox
                        checked={queueSelectedImages.has(imageUrl)}
                        onCheckedChange={(checked) => handleQueueImageSelect(item.id, imageUrl, checked as boolean)}
                        className="bg-black/50 border-white/50"
                      />
                    </div>
                    <div className="aspect-square overflow-hidden rounded-lg border border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer group-hover:scale-[1.02]">
                        <img
                          src={imageUrl}
                          alt={`Generated ${index + 1}`}
                          className="w-full h-full object-contain"
                          onClick={() => setViewingImage(imageUrl)}
                      />
                    </div>
                    <div className="absolute bottom-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-black/70 border-white/30 hover:border-white/50 text-white hover:bg-black/80 p-2"
                        onClick={() => setViewingImage(imageUrl)}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-black/70 border-white/30 hover:border-white/50 text-white hover:bg-black/80 p-2"
                        onClick={() => downloadImage(imageUrl, `generated-${Date.now()}-${index + 1}.png`)}
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Authentication Required</h3>
        <p className="text-gray-300">Please sign in to view your queue</p>
      </div>
    );
  }

  const allCompletedImages = completedItems.flatMap(item => 
    item.generatedImages.map(img => img.url).filter(Boolean)
  );
  const selectedCount = selectedImages.size;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent mb-2">
            Queue Monitor
          </h2>
          <p className="text-gray-300">Track your image generation requests in real-time</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {selectedCount > 0 && (
            <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-xl border border-white/20">
              <span className="text-sm text-white font-medium">{selectedCount} selected</span>
              <Button
                variant="outline"
                onClick={() => handleBulkDownload(Array.from(selectedImages), false)}
                disabled={isDownloading}
                className="border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Individual
              </Button>
              <Button
                variant="outline"
                onClick={() => handleBulkDownload(Array.from(selectedImages), true)}
                disabled={isDownloading}
                className="border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white"
              >
                <Archive className="w-4 h-4 mr-2" />
                ZIP
              </Button>
            </div>
          )}
          {queue.length > 0 && (
            <Button
              onClick={clearQueue}
              variant="outline"
              className="border-red-500/50 hover:border-red-500 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-transparent border-yellow-500/30 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-300 font-medium">Active Requests</p>
                <p className="text-2xl font-bold text-white">{activeItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-transparent border-green-500/30 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-300 font-medium">Completed</p>
                <p className="text-2xl font-bold text-white">{completedItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-transparent border-red-500/30 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-red-500/20 rounded-xl">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-300 font-medium">Errors</p>
                <p className="text-2xl font-bold text-white">{errorItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-transparent border-blue-500/30 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <ImageIcon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-300 font-medium">Total Images</p>
                <p className="text-2xl font-bold text-white">{allCompletedImages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Queue Items */}
      {queue.length === 0 ? (
        <Card className="bg-transparent border-white/20 backdrop-blur-sm">
          <CardContent className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <ImageIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">No requests in queue</h3>
            <p className="text-gray-300 text-lg">Start generating images to see your requests here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Active Items */}
          {activeItems.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <RefreshCcw className="w-5 h-5 text-blue-400" />
                <span>Active Requests ({activeItems.length})</span>
              </h3>
              <div className="space-y-6">
                {activeItems.map(renderQueueItem)}
              </div>
            </div>
          )}

          {/* Completed Items */}
          {completedItems.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Completed Requests ({completedItems.length})</span>
              </h3>
              <div className="space-y-6">
                {completedItems.map(renderQueueItem)}
              </div>
            </div>
          )}

          {/* Error Items */}
          {errorItems.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span>Failed Requests ({errorItems.length})</span>
              </h3>
              <div className="space-y-6">
                {errorItems.map(renderQueueItem)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewingImage} onOpenChange={() => setViewingImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-black/90 border-white/20">
          {viewingImage && (
            <div className="flex items-center justify-center">
              <img
                src={viewingImage}
                alt="Generated Image"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QueueMonitor;
